import { useState } from "react";
import { Loader2, RotateCcw, ShieldCheck, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const RIGHTS = [
  ["can_manage_users", "Can Manage Users (Add / Edit)"],
  ["can_delete_users", "Can Soft-Delete Employees"],
  ["can_purge_users", "Can Permanently Purge Dummy Employees"],
  ["can_delete_customers", "Allow Customer Deletion (Soft / Hard)"],
  ["can_approve_customer_edits", "Allow Direct Approval of Customer Edits"],
  ["can_export_contacts", "Can request customer / lead export (still needs the Owner's OTP)"],
  ["can_export_ca_bills", "Can export CA / GST bills (.ZIP)"],
];

/** Owner-grade editor: profile, role, cost access and delegated user-management rights. */
export function UserEditDialog({ employee, roles, isOwner, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: employee.name || "", email: employee.email || "",
    whatsapp: employee.whatsapp || "", mobile: employee.mobile || "",
    role: employee.role || "employee", department: employee.department || "",
    designation: employee.designation || "", location: employee.location || "",
    cost_access: employee.cost_access || "none",
    can_manage_users: !!employee.can_manage_users && !employee.is_owner,
    can_delete_users: !!employee.can_delete_users && !employee.is_owner,
    can_purge_users: !!employee.can_purge_users && !employee.is_owner,
    can_delete_customers: !!employee.can_delete_customers && !employee.is_owner,
    can_approve_customer_edits: !!employee.can_approve_customer_edits && !employee.is_owner,
    can_export_contacts: !!employee.can_export_contacts && !employee.is_owner,
    can_export_ca_bills: !!employee.can_export_ca_bills && !employee.is_owner,
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim())
      return toast.error("Name and email are required");
    setSaving(true);
    try {
      await api.put(`/user-admin/employees/${employee.id}`, form);
      if (isOwner && !employee.is_owner)
        await api.put(`/secure-export/rights/${employee.id}`, {
          can_export_contacts: form.can_export_contacts,
          can_export_ca_bills: form.can_export_ca_bills,
        });
      toast.success(`${form.name} updated`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl"
        data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit user — {employee.name}</DialogTitle>
          <DialogDescription>
            Their attendance, salary and work history stays exactly as it is.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          {[["name", "Name"], ["email", "Email"], ["whatsapp", "WhatsApp number"],
            ["mobile", "Mobile"], ["department", "Department"], ["designation", "Designation"],
            ["location", "Factory / site"]].map(([k, l]) => (
            <div key={k}>
              <div className="overline">{l}</div>
              <Input data-testid={`user-${k}-input`} value={form[k]}
                onChange={(e) => set({ [k]: e.target.value })} />
            </div>
          ))}
          <div>
            <div className="overline">Role</div>
            <Select value={form.role} onValueChange={(v) => set({ role: v })}
              disabled={employee.is_owner}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {roles.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="overline">Inventory cost access</div>
            <Select value={form.cost_access} onValueChange={(v) => set({ cost_access: v })}>
              <SelectTrigger data-testid="user-cost-access-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No cost access</SelectItem>
                <SelectItem value="view">Can see cost</SelectItem>
                <SelectItem value="edit">Can edit cost</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded border border-border p-3" data-testid="user-rights-block">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <ShieldCheck className="h-4 w-4" /> User-management rights
          </div>
          {employee.is_owner ? (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="owner-rights-note">
              This is the Owner account — it always has every right and cannot be edited or
              removed by anyone else.
            </p>
          ) : isOwner ? (
            <div className="mt-2 space-y-2">
              {RIGHTS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-xs">
                  <Checkbox checked={form[key]} data-testid={`right-${key}`}
                    onCheckedChange={(v) => set({ [key]: !!v })} />
                  {label}
                </label>
              ))}
              <p className="text-[11px] text-muted-foreground">
                Leave these unticked and this person will never see the Edit or Delete buttons.
              </p>
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="rights-locked-note">
              Only the Owner can grant these rights.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="user-edit-save">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Deactivation with a mandatory reason; also revokes WhatsApp bot access. */
export function UserDeactivateDialog({ employee, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const presets = ["No longer with the company", "Wrong or incorrect entry",
                   "Duplicate employee record", "On long leave — access paused"];

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/user-admin/employees/${employee.id}/deactivate`,
        { reason });
      toast.success(`${data.name} deactivated`);
      if (data.whatsapp_revoked) toast.info(`WhatsApp ${data.whatsapp} can no longer use the AI assistant`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-deactivate-dialog">
        <DialogHeader>
          <DialogTitle>Deactivate {employee.name}?</DialogTitle>
          <DialogDescription>
            Their app login stops immediately, their devices are signed out, and the AI WhatsApp
            assistant will stop recognising {employee.whatsapp || "their number"}. Attendance,
            salary and work history are all kept, and you can restore them any time.
          </DialogDescription>
        </DialogHeader>
        <Input placeholder="Why? (kept with the record as proof)" value={reason}
          data-testid="deactivate-reason-input" onChange={(e) => setReason(e.target.value)} />
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button key={p} type="button"
              data-testid={`deactivate-reason-${p.split(" ")[0].toLowerCase()}`}
              className="rounded-full border px-2.5 py-1 text-[11px] hover:bg-accent"
              onClick={() => setReason(p)}>{p}</button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Keep active</Button>
          <Button variant="destructive" onClick={go} disabled={busy}
            data-testid="deactivate-confirm">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InactiveUserActions({ employee, canDelete, canPurge, onChanged }) {
  const [busy, setBusy] = useState(false);

  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/user-admin/employees/${employee.id}/restore`, {});
      toast.success(`${data.name} restored — login and WhatsApp access are back on`);
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const purge = async () => {
    const reason = window.prompt(
      `Delete ${employee.name} forever? Type the reason (cannot be undone):`, "");
    if (!reason) return;
    setBusy(true);
    try {
      const { data } = await api.delete(
        `/user-admin/employees/${employee.id}/purge?reason=${encodeURIComponent(reason)}`);
      toast.success(`${data.name} permanently deleted`);
      onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex justify-end gap-1.5">
      {canDelete && (
        <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]"
          disabled={busy} data-testid={`restore-user-${employee.id}`} onClick={restore}>
          <RotateCcw className="h-3 w-3" /> Restore
        </Button>
      )}
      {canPurge && (
        <Button size="sm" variant="ghost" disabled={busy}
          className="h-7 gap-1 px-2 text-[11px] text-destructive"
          data-testid={`purge-user-${employee.id}`} onClick={purge}>
          <Trash2 className="h-3 w-3" /> Delete forever
        </Button>
      )}
      {!canDelete && !canPurge && (
        <Badge variant="outline" className="text-[10px]">view only</Badge>
      )}
    </div>
  );
}
